import {
  GEOCODER_BIAS_POINT,
  GEOCODER_FALLBACK_HALF_SIZE_LAT_DEGREES,
  GEOCODER_FALLBACK_HALF_SIZE_LNG_DEGREES
} from 'js/lib/planner/geocoder-fallback-area'

// Photon default. Values >= 0.8 cancel the proximity bias (far villages outrank the city center).
// Identical displayed labels (which include the locality) are indistinguishable to the
// user, so two OSM ways of the same place within this distance collapse into one entry.
export const DEDUPE_DISTANCE_METERS = 300
const METERS_PER_DEGREE = 111320

// Leading street-type words (after accent/case normalization) mapped to a canonical token.
// "ç" is only folded to "z" here (Plaça -> Plaza), never globally.
const STREET_TYPES = {
  plaza: 'plaza', placa: 'plaza', pl: 'plaza',
  calle: 'calle', carrer: 'calle', c: 'calle', cl: 'calle',
  avenida: 'avenida', avinguda: 'avenida', avda: 'avenida', av: 'avenida',
  paseo: 'paseo', passeig: 'paseo',
  camino: 'camino', cami: 'camino',
  carretera: 'carretera', ctra: 'carretera'
}

function normalizeLocationName(name) {
  const normalized = String(name == null ? '' : name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

  const match = normalized.match(/^([a-z]+)\.?(?:\/|\s|$)\s*(.*)$/)
  const canonical = match && STREET_TYPES[match[1]]

  return canonical ? `${canonical} ${match[2]}`.trim() : normalized
}

// Accepts GeoJSON [lon, lat] or {lat, lng}; returns {lat, lon} or null
function toLocationPoint(coordinates) {
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    const lon = Number(coordinates[0])
    const lat = Number(coordinates[1])
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null
  }
  if (coordinates && typeof coordinates === 'object') {
    const lat = Number(coordinates.lat)
    const lon = Number(coordinates.lng)
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null
  }
  return null
}

function isSameSpot(pointA, pointB) {
  if (pointA === null || pointB === null) return pointA === pointB

  const meanLat = ((pointA.lat + pointB.lat) / 2) * Math.PI / 180
  const dx = (pointA.lon - pointB.lon) * Math.cos(meanLat) * METERS_PER_DEGREE
  const dy = (pointA.lat - pointB.lat) * METERS_PER_DEGREE

  return Math.sqrt(dx * dx + dy * dy) <= DEDUPE_DISTANCE_METERS
}

/*
 * Order-preserving deduplication of geocoder results. Two entries are duplicates
 * when their displayed name (case, accents and whitespace insensitive) is the same
 * and their coordinates are within DEDUPE_DISTANCE_METERS (or both lack coordinates). The first
 * occurrence wins so the proximity ranking is untouched. Entries of `seed` count as
 * already shown (they are not returned), which allows removing cross-list duplicates.
 */
export function dedupeLocations(list, seed = []) {
  if (!Array.isArray(list)) return []

  const seen = new Map()
  const register = item => {
    const key = normalizeLocationName(item.finalName || item.name)
    const point = toLocationPoint(item.coordinates)
    if (!seen.has(key)) seen.set(key, [])
    seen.get(key).push(point)
  }
  const isSeen = item => {
    const points = seen.get(normalizeLocationName(item.finalName || item.name))
    if (!points) return false
    const point = toLocationPoint(item.coordinates)
    return points.some(known => isSameSpot(known, point))
  }

  ;(Array.isArray(seed) ? seed : []).forEach(item => item && register(item))

  return list.filter(item => {
    if (!item || isSeen(item)) return false
    register(item)
    return true
  })
}

export const GEOCODER_LOCATION_BIAS_SCALE = 0.2

// Photon is a shared public service: wait for the user to stop typing and do not
// query it for fragments too short to be meaningful (COEGIS1-15259).
export const GEOCODER_SEARCH_DEBOUNCE_MS = 500
export const GEOCODER_MIN_QUERY_LENGTH = 3

export function countConsecutiveMatches(searchTerm, stopName) {
  const regex = new RegExp(searchTerm, 'ig')
  const match = stopName.match(regex)
  return match ? match.length : 0
}

export function filterCustomStops(stops, searchTerm, limit = 20) {
  if (!searchTerm || searchTerm.trim() === '') {
    return []
  }

  const normalizedSearchTerm = searchTerm.toLowerCase()
  const sanitizedSearchTerm = normalizedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  try {
    const regex = new RegExp(sanitizedSearchTerm, 'i')

    return stops
      ?.filter(stop => regex.test(stop.name.toLowerCase()))
      .sort((a, b) => {
        const aMatchCount = countConsecutiveMatches(sanitizedSearchTerm, a.name.toLowerCase())
        const bMatchCount = countConsecutiveMatches(sanitizedSearchTerm, b.name.toLowerCase())
        return bMatchCount - aMatchCount
      })
      .slice(0, limit)
  } catch (error) {
    console.error('Error en la expresión regular:', error)
    return []
  }
}

export function buildSearchDirectionResults(response, limit = 20) {
  const results = response.body.features.map(({ properties, geometry }) => {
    const { street, name, city, housenumber } = properties
    // A named place on a street (e.g. "Ayuntamiento" on "Carrer Major") keeps its
    // name; street features (name equal to the street) and unnamed results keep
    // the plain street label.
    const isPlaceOnStreet = name && street &&
      normalizeLocationName(name) !== normalizeLocationName(street)
    const placeName = isPlaceOnStreet ? name : false
    const formattedName = [
      placeName,
      street || name,
      housenumber || false,
      city
    ].filter(Boolean).join(', ')

    return {
      name: formattedName,
      coordinates: geometry.coordinates
    }
  })

  return dedupeLocations(results).slice(0, limit)
}

export function getFinalLocationName(properties) {
  const { name, street, housenumber, city } = properties
  const formattedName = [
    name || false,
    street,
    housenumber || false,
    city || false
  ].filter(Boolean).join(', ')

  return formattedName.trim() !== '' ? formattedName : null
}

export function extractPointOfInterestResults(response, limit = 20) {
  if (!response.body || !response.body.features || !Array.isArray(response.body.features)) {
    return []
  }

  const results = response.body.features
    .map(feature => {
      const { properties, geometry } = feature
      return {
        osm_value: properties?.osm_value || null,
        osm_key: properties?.osm_key || null,
        nameCategory: properties?.name || null,
        name: getFinalLocationName(properties) || null,
        coordinates: geometry?.coordinates || null,
        housenumber: properties?.housenumber || null,
        finalName: getFinalLocationName(properties) || null
      }
    })

  return dedupeLocations(results).slice(0, limit)
}

export function buildRecentSearches(searchList, location, limit = 20) {
  const nextSearchList = searchList.filter(item => item.name !== location.name ||
    item.coordinates[0] !== location.coordinates[0] ||
    item.coordinates[1] !== location.coordinates[1])

  nextSearchList.push(location)

  if (nextSearchList.length > limit) {
    nextSearchList.shift()
  }

  return nextSearchList
}

export function getPointOfInterestIconClass(properties, validIcons) {
  const { osm_key, osm_value } = properties
  const genericClass = 'icon-directions-default'

  if (osm_key && osm_value) {
    if (validIcons[osm_key] && validIcons[osm_key].includes(osm_value)) {
      return `ns-icon-${osm_key}-${osm_value}`
    }
  }

  return genericClass
}

export function getSearchDirectionState(activeField, originDirection, destinationDirection) {
  const isOrigin = activeField === 'origin'

  return {
    isOrigin,
    searchTerm: isOrigin ? originDirection : destinationDirection,
    directionField: isOrigin ? 'originDirection' : 'destinationDirection',
    loadingField: isOrigin ? 'isLoadingOrigin' : 'isLoadingDestination'
  }
}

export function buildSearchDirectionsParams(searchTerm, routerUrl, bbox, limit = 20, lat = null, lon = null, userPosition = null) {
  return {
    paramsFilter: searchTerm,
    routerUrl,
    bbox,
    limit,
    lat,
    lon,
    userPosition
  }
}

export function removeFunctionalInterceptors(interceptors) {
  const removedInterceptors = []

  interceptors.forEach((interceptor, index) => {
    if (typeof interceptor === 'function') {
      removedInterceptors.push(interceptor)
      interceptors.splice(index, 1)
    }
  })

  return removedInterceptors
}

/*
 * Returns the south-west / north-east corners of a rectangle defined by two
 * opposite corners, whatever their order. Municipality bounds are stored as
 * upper-left / lower-right and some municipalities (e.g. Valencia) have the
 * longitudes swapped, while google.maps.LatLngBounds expects (southwest, northeast).
 */
export function getSortedBoundsCorners(cornerA, cornerB) {
  const latA = Number(cornerA.lat)
  const lngA = Number(cornerA.lng)
  const latB = Number(cornerB.lat)
  const lngB = Number(cornerB.lng)

  return {
    southWest: { lat: Math.min(latA, latB), lng: Math.min(lngA, lngB) },
    northEast: { lat: Math.max(latA, latB), lng: Math.max(lngA, lngB) }
  }
}

function toFiniteNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/*
 * Coerces `{lat, lng}` (numbers or numeric strings) to numbers. Returns null for
 * missing/non-numeric values, out-of-range coordinates (lat outside [-90, 90] or
 * lng outside [-180, 180], e.g. 999/999 or 2131/1231 found in DB) and the (0, 0)
 * "null island" placeholder (COEGIS1-15259).
 */
function toValidPoint(point) {
  if (point == null || typeof point !== 'object') return null
  const lat = toFiniteNumber(point.lat)
  const lng = toFiniteNumber(point.lng)
  if (lat === null || lng === null) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

function getBoundsRange(bounds) {
  if (bounds == null) return null

  const northEast = bounds.getNorthEast()
  const southWest = bounds.getSouthWest()
  const lat1 = northEast.lat()
  const lng1 = northEast.lng()
  const lat2 = southWest.lat()
  const lng2 = southWest.lng()

  return {
    minLat: Math.min(lat1, lat2),
    maxLat: Math.max(lat1, lat2),
    minLon: Math.min(lng1, lng2),
    maxLon: Math.max(lng1, lng2)
  }
}

function isInsideRange(point, range) {
  return !range || (
    point.lat >= range.minLat && point.lat <= range.maxLat &&
    point.lng >= range.minLon && point.lng <= range.maxLon)
}

/*
 * Resolves the point the geocoder results are biased towards:
 *  1. the user position, if it is inside the bounds (edges included);
 *  2. the tenant anchor (GEOCODER_BIAS_POINT), only if it is valid and, when there
 *     are bounds, inside them (edges included); null for most tenants;
 *  3. the municipality center (municipality `geo` lat/lng), only if it is valid
 *     and, when there are bounds, inside them (edges included): some municipalities
 *     have a `geo` far away from their real bounds (COEGIS1-15259);
 *  4. last resort: the arithmetic mean of the bounds.
 * Returns `{lat, lon}` or null when nothing can be resolved.
 */
export function resolveGeocoderBiasPoint({ bounds, userPosition, municipalityCenter } = {}) {
  const range = getBoundsRange(bounds)
  const user = toValidPoint(userPosition)

  if (range && user &&
    user.lat >= range.minLat && user.lat <= range.maxLat &&
    user.lng >= range.minLon && user.lng <= range.maxLon) {
    return { lat: user.lat, lon: user.lng }
  }

  const anchor = toValidPoint(GEOCODER_BIAS_POINT)
  if (anchor && isInsideRange(anchor, range)) return { lat: anchor.lat, lon: anchor.lng }

  const center = toValidPoint(municipalityCenter)
  if (center && isInsideRange(center, range)) {
    return { lat: center.lat, lon: center.lng }
  }

  if (range) {
    return { lat: (range.minLat + range.maxLat) / 2, lon: (range.minLon + range.maxLon) / 2 }
  }

  return null
}

function roundDegrees(value) {
  return Math.round(value * 1e6) / 1e6
}

/*
 * Corners of the rectangle around a valid municipality center, used when the
 * municipality has no usable bounds (per-axis sizes: geocoder-fallback-area).
 */
function buildFallbackCorners(center) {
  return {
    southWest: {
      lat: roundDegrees(center.lat - GEOCODER_FALLBACK_HALF_SIZE_LAT_DEGREES),
      lng: roundDegrees(center.lng - GEOCODER_FALLBACK_HALF_SIZE_LNG_DEGREES)
    },
    northEast: {
      lat: roundDegrees(center.lat + GEOCODER_FALLBACK_HALF_SIZE_LAT_DEGREES),
      lng: roundDegrees(center.lng + GEOCODER_FALLBACK_HALF_SIZE_LNG_DEGREES)
    }
  }
}

/*
 * Rectangle around the municipality center, used when the municipality has no
 * usable bounds. Returns a bounds-like object (same shape as google.maps.LatLngBounds)
 * or null when there is no valid center.
 */
function buildFallbackBounds(municipalityCenter) {
  const center = toValidPoint(municipalityCenter)
  if (!center) return null

  const { southWest, northEast } = buildFallbackCorners(center)

  return {
    getNorthEast: () => ({ lat: () => northEast.lat, lng: () => northEast.lng }),
    getSouthWest: () => ({ lat: () => southWest.lat, lng: () => southWest.lng })
  }
}

/*
 * Corners (southWest, northEast) of the municipality area used to bias Google
 * Autocomplete (COEGIS1-15259):
 *  1. both stored corners are valid and describe a real rectangle (different
 *     latitude and longitude): they are used, sorted;
 *  2. otherwise a valid municipality center gives the fallback rectangle;
 *  3. otherwise degenerate corners from the raw center (never throws).
 */
export function getMunicipalityBoundsCorners({ upperLeft, lowerRight, municipalityCenter } = {}) {
  const cornerA = toValidPoint(upperLeft)
  const cornerB = toValidPoint(lowerRight)

  if (cornerA && cornerB && cornerA.lat !== cornerB.lat && cornerA.lng !== cornerB.lng) {
    return getSortedBoundsCorners(cornerA, cornerB)
  }

  const center = toValidPoint(municipalityCenter)
  if (center) return buildFallbackCorners(center)

  const rawCenter = municipalityCenter || {}
  return getSortedBoundsCorners(rawCenter, rawCenter)
}

/*
 * Builds the geocoder (Photon) location bias from google.maps.LatLngBounds-like
 * bounds: bbox as `minLon,minLat,maxLon,maxLat` (the municipality bounds) and
 * lat/lon from resolveGeocoderBiasPoint (never getCenter(), which is wrong
 * for bounds that cross the antimeridian). When the bounds are missing or do not
 * describe a rectangle (same latitude or longitude on both corners) a fallback
 * rectangle around `municipalityCenter` is used instead (COEGIS1-15259). Invalid
 * centers (out of range or 0,0) are ignored, so without real bounds nor a valid
 * center this returns null. A center outside the real bounds is not used as bias
 * point (the bounds mean is used instead).
 */
export function buildGeocoderLocationBias(bounds, { userPosition, municipalityCenter } = {}) {
  let effectiveBounds = bounds
  let range = getBoundsRange(effectiveBounds)

  if (range === null || range.minLat === range.maxLat || range.minLon === range.maxLon) {
    effectiveBounds = buildFallbackBounds(municipalityCenter)
    range = getBoundsRange(effectiveBounds)
    if (range === null) return null
  }

  const { lat, lon } = resolveGeocoderBiasPoint({ bounds: effectiveBounds, userPosition, municipalityCenter })

  return {
    bbox: `${range.minLon},${range.minLat},${range.maxLon},${range.maxLat}`,
    lat,
    lon
  }
}

/*
 * Builds the lists rendered by the planner dropdown (in display order: locations,
 * recent searches, custom stops, points of interest) so that an entry is shown
 * only once across all of them. The first list in display order keeps the entry.
 */
export function buildDropdownSections({ locations, recentSearches, customStops, pointsOfInterest, searchTerm } = {}) {
  const shownLocations = dedupeLocations(locations)
  const shownRecent = dedupeLocations(recentSearches, shownLocations)
  const shownStops = dedupeLocations(customStops, [...shownLocations, ...shownRecent])
  const shownPoints = dedupeLocations(pointsOfInterest, [...shownLocations, ...shownRecent, ...shownStops])

  return {
    locations: shownLocations,
    recentSearches: shownRecent,
    customStops: shownStops,
    pointsOfInterest: shownPoints,
    // The "no recent searches" empty state only applies to an empty search term
    showNoRecentSearchesMessage: String(searchTerm == null ? '' : searchTerm).trim() === '' &&
      (Array.isArray(recentSearches) ? recentSearches.length : 0) === 0
  }
}

const GEOCODER_QUERY_CONNECTORS = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'les', 'dels'])

function foldToken(token) {
  return token.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

/*
 * Query sent to the geocoder (Photon). Its full-text matching cannot match
 * "del Ayuntamiento" with "de l'Ajuntament", so standalone connectors
 * (de, del, la, las, los, el, les, dels) and a leading l'/d' are removed.
 * Tokens with digits are always kept. If fewer than 2 tokens remain (the user is
 * still typing) the original trimmed term is returned unchanged.
 */
export function normalizeGeocoderQuery(term) {
  if (typeof term !== 'string') return ''

  const original = term.trim()
  const kept = original.split(/\s+/).filter(Boolean).reduce((tokens, token) => {
    if (/\d/.test(token)) return tokens.concat(token)

    const stripped = token.replace(/^[ldLD]['\u2019]/, '')
    if (!stripped || GEOCODER_QUERY_CONNECTORS.has(foldToken(stripped))) return tokens

    return tokens.concat(stripped)
  }, [])

  return kept.length < 2 ? original : kept.join(' ')
}

// Words that mean the same in the two languages of the geocoder data (folded:
// no accents, lower case). Both directions are listed explicitly.
const GEOCODER_SYNONYMS = {
  ayuntamiento: 'ajuntament',
  ajuntament: 'ayuntamiento'
}

/*
 * Query variants sent to the geocoder: the normalized query plus, when a whole
 * word has a synonym (GEOCODER_SYNONYMS), the query with that word swapped.
 * At most 2 variants; one when there is no synonym.
 */
export function getGeocoderQueryVariants(term) {
  const query = normalizeGeocoderQuery(term)
  const tokens = query.split(/\s+/)
  let swapped = false

  const variantTokens = tokens.map(token => {
    const synonym = GEOCODER_SYNONYMS[foldToken(token)]
    if (!synonym) return token
    swapped = true
    return synonym
  })

  return swapped ? [query, variantTokens.join(' ')] : [query]
}

function getFeaturePoint(feature) {
  const coordinates = feature && feature.geometry && feature.geometry.coordinates
  return toLocationPoint(coordinates)
}

function distanceMeters(pointA, pointB) {
  const meanLat = ((pointA.lat + pointB.lat) / 2) * Math.PI / 180
  const dx = (pointA.lon - pointB.lon) * Math.cos(meanLat) * METERS_PER_DEGREE
  const dy = (pointA.lat - pointB.lat) * METERS_PER_DEGREE
  return Math.sqrt(dx * dx + dy * dy)
}

/*
 * Photon ranks exact-name matches (a townhall literally named "Ayuntamiento" in a
 * small town) above proximity. Stable two-tier sort: tier 0 = feature name equal
 * to or starting with a query variant (accent/case insensitive), tier 1 = the
 * rest; each tier ordered by distance to the bias point, features without
 * coordinates last of their tier. Returns a new array; the input is not mutated.
 * With an invalid bias point the features are returned unchanged.
 */
export function rankFeaturesByProximity(features, bias, queryVariants = []) {
  if (!Array.isArray(features)) return []

  const biasLat = bias ? toFiniteNumber(bias.lat) : null
  const biasLon = bias ? toFiniteNumber(bias.lon) : null
  if (biasLat === null || biasLon === null) return features

  const biasPoint = { lat: biasLat, lon: biasLon }

  const variants = (Array.isArray(queryVariants) ? queryVariants : [])
    .map(normalizeLocationName)
    .filter(Boolean)

  return features
    .map((feature, index) => {
      const name = normalizeLocationName(feature && feature.properties && feature.properties.name)
      const point = getFeaturePoint(feature)
      return {
        feature,
        index,
        tier: name && variants.some(variant => name.startsWith(variant)) ? 0 : 1,
        distance: point ? distanceMeters(point, biasPoint) : Infinity
      }
    })
    .sort((a, b) => a.tier - b.tier || (a.distance === b.distance ? 0 : a.distance - b.distance) || a.index - b.index)
    .map(item => item.feature)
}
